import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import PauseIcon from '@mui/icons-material/Pause';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';

// Background alpha is high enough that the soft circle stays visible on dark
// backgrounds (a 12% tint vanishes on near-black) while reading soft on light.
const tint = (theme, key) => alpha(theme.palette[key].main, 0.22);

const STATES = {
  synced: {
    getBg: theme => tint(theme, 'success'),
    color: 'success.main',
    Icon: CheckIcon,
    labelKey: 'DocumentsScene.SyncState.synced',
  },
  syncing: {
    getBg: theme => tint(theme, 'primary'),
    color: 'primary.main',
    Icon: SyncIcon,
    labelKey: 'DocumentsScene.SyncState.syncing',
  },
  error: {
    getBg: theme => tint(theme, 'error'),
    color: 'error.main',
    Icon: WarningAmberIcon,
    labelKey: 'DocumentsScene.SyncState.error',
    detailKey: 'DocumentsScene.SyncState.error_detail',
  },
  needs_reauth: {
    getBg: theme => tint(theme, 'error'),
    color: 'error.main',
    Icon: WarningAmberIcon,
    labelKey: 'DocumentsScene.SyncState.needs_reauth',
    detailKey: 'DocumentsScene.SyncState.needs_reauth_detail',
  },
  paused: {
    getBg: theme => alpha(theme.palette.text.secondary, 0.2),
    color: 'text.secondary',
    Icon: PauseIcon,
    labelKey: 'DocumentsScene.SyncState.paused',
  },
};

// Map a cloud-mirror mapping to the badge state to show. The backend only
// emits idle|syncing|error|needs_reauth (+ a `paused` flag), so we derive the
// design's "synced" (idle but already synced once) and surface paused here.
export const mirrorDisplayState = mirror => {
  if (!mirror) return null;
  if (mirror.paused) return 'paused';
  if (mirror.syncState === 'idle') return mirror.lastSyncedAt ? 'synced' : null;
  return mirror.syncState || null;
};

const SyncBadge = ({ syncState = null }) => {
  if (!syncState || syncState === 'idle') return null;
  const cfg = STATES[syncState] || STATES.error;
  const label = I18n.t(cfg.labelKey);
  const detail = cfg.detailKey ? I18n.t(cfg.detailKey) : null;

  return (
    <Tooltip title={detail ? `${label} — ${detail}` : label} placement="top">
      <Box
        component="span"
        data-testid="sync-badge"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: theme => cfg.getBg(theme),
          color: cfg.color,
          flexShrink: 0,
          cursor: 'default',
        }}
      >
        <cfg.Icon sx={{ fontSize: 12 }} />
      </Box>
    </Tooltip>
  );
};

SyncBadge.propTypes = { syncState: string };

export default SyncBadge;
